/**
 * Sign in.
 *
 * The screen that made the console usable by a person. Everything else read a token out of
 * `localStorage` that nothing ever wrote.
 *
 * ═══ The captcha is asked about, never assumed ════════════════════════════════
 *
 * Cloudflare Turnstile is configured in production and absent locally, so a form that hard-codes
 * either answer is broken in one of the two places — and the broken direction is the production one,
 * where the server refuses every sign-in with `captchaRequired` and there is no widget on screen to
 * satisfy it. `GET /api/auth/captcha-config` is the server telling us which deployment this is.
 *
 * The Turnstile script is loaded only when a site key comes back. A console that pulls a third-party
 * script on every visit of a deployment that does not use it is a needless dependency on someone
 * else's CDN being up.
 *
 * ═══ Deliberately not helpful about failures ══════════════════════════════════
 *
 * The server answers a wrong password and a throttled account with the SAME message, so that neither
 * confirms an address is worth attacking. Its wording is shown verbatim. Rewording it into something
 * friendlier and more specific would undo the protection.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Icon } from "../components/Icon";
import { GoogleButton } from "../components/GoogleButton";
import {
  signIn, fetchCaptchaConfig, fetchSsoConfig, signInWithGoogle,
  SignInError, type CaptchaConfig, type SsoConfig,
} from "../lib/session";

declare global {
  interface Window {
    turnstile?: {
      render: (el: HTMLElement, opts: Record<string, unknown>) => string;
      reset: (id?: string) => void;
    };
  }
}

const TURNSTILE_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

/** Load the Turnstile script once, and only if this deployment uses it. */
function loadTurnstile(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  const existing = document.querySelector<HTMLScriptElement>(`script[src="${TURNSTILE_SRC}"]`);
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("turnstile failed to load")));
    });
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = TURNSTILE_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("turnstile failed to load"));
    document.head.appendChild(script);
  });
}

export function SignInPage({
  onSignedIn,
  onCreateAccount,
}: {
  onSignedIn: () => void;
  onCreateAccount: () => void;
}) {
  const [sso, setSso] = useState<SsoConfig | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryAfter, setRetryAfter] = useState<number | null>(null);

  const [captcha, setCaptcha] = useState<CaptchaConfig | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaBroken, setCaptchaBroken] = useState(false);
  const widgetHost = useRef<HTMLDivElement | null>(null);
  const widgetId = useRef<string | null>(null);

  useEffect(() => {
    void fetchCaptchaConfig().then(setCaptcha);
    void fetchSsoConfig().then(setSso);
  }, []);

  /*
   * Google sign-in carries no captcha token, and does not need one.
   *
   * Turnstile exists to make guessing expensive. There is nothing to guess here: a caller either holds
   * a Google-signed token audienced to this application or they do not, and repetition does not produce
   * one. The server route is deliberately free of `requireCaptcha` for the same reason.
   */
  const submitGoogle = async (credential: string) => {
    setBusy(true);
    setError(null);
    setRetryAfter(null);
    try {
      await signInWithGoogle(credential, "email");
      onSignedIn();
    } catch (err) {
      setError(
        err instanceof SignInError
          ? err.message
          : "Could not reach PulseAssist. Check your connection and try again.",
      );
    } finally {
      setBusy(false);
    }
  };

  // Render the widget once the config says it is needed and the host div exists.
  useEffect(() => {
    if (!captcha?.enabled || !captcha.siteKey || !widgetHost.current || widgetId.current) return;
    let cancelled = false;

    void loadTurnstile()
      .then(() => {
        if (cancelled || !widgetHost.current || !window.turnstile) return;
        widgetId.current = window.turnstile.render(widgetHost.current, {
          sitekey: captcha.siteKey,
          callback: (token: string) => setCaptchaToken(token),
          // A token that expires unused must not be submitted — the server would reject it and the
          // person would see a failure they cannot connect to anything they did.
          "expired-callback": () => setCaptchaToken(null),
          "error-callback": () => setCaptchaBroken(true),
          theme: document.documentElement.dataset.theme === "dark" ? "dark" : "light",
        });
      })
      .catch(() => setCaptchaBroken(true));

    return () => { cancelled = true; };
  }, [captcha]);

  const resetCaptcha = useCallback(() => {
    setCaptchaToken(null);
    try {
      window.turnstile?.reset(widgetId.current ?? undefined);
    } catch {
      /* the widget will be re-rendered on the next mount */
    }
  }, []);

  const submit = async () => {
    setBusy(true);
    setError(null);
    setRetryAfter(null);
    try {
      await signIn(email.trim(), password, captchaToken);
      onSignedIn();
    } catch (err) {
      if (err instanceof SignInError) {
        setError(err.message);
        setRetryAfter(err.retryAfterSeconds);
        /*
         * A Turnstile token is single-use. Whether the sign-in failed on the captcha or on the
         * password, the token is spent, so the widget is reset for the next attempt — otherwise the
         * second try fails on a consumed token and the message stops matching the cause.
         */
        if (captcha?.enabled) resetCaptcha();
      } else {
        setError("Could not reach PulseAssist. Check your connection and try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  const captchaOutstanding = Boolean(captcha?.enabled) && !captchaBroken && !captchaToken;
  const canSubmit = email.trim().length > 0 && password.length > 0 && !busy && !captchaOutstanding;

  return (
    <div
      className="grid min-h-screen place-items-center px-4"
      style={{ background: "var(--canvas)" }}
    >
      <main className="w-full" style={{ maxWidth: 380 }}>
        <div className="mb-7 flex items-center gap-[10px]">
          {/* The one place the ecosystem gradient appears; the console itself is flat. */}
          <span
            aria-hidden="true"
            className="block h-[26px] w-[26px] shrink-0 rounded-[6px]"
            style={{ background: "linear-gradient(135deg,#2563EB,#7C3AED)" }}
          />
          <span style={{ fontSize: "var(--text-section)" }}>
            <b style={{ fontWeight: 600 }}>PulseAssist</b>{" "}
            <span style={{ color: "var(--accent)", fontWeight: 500 }}>Email</span>
          </span>
        </div>

        <h1 style={{ fontSize: "var(--text-page)", fontWeight: 600, letterSpacing: "-0.01em" }}>
          Sign in
        </h1>
        {/*
          The old copy read "Use your PulseAssist account. The same one you use for the dashboard." That
          was true for existing AI customers and actively wrong for everybody else: PulseAssist Email is
          sold on its own, and telling a prospective Email customer they need a dashboard account is
          telling them the product has a prerequisite it does not have.

          One login DOES span both products, which is why the second sentence still says so — an
          existing AI customer should not go looking for a separate password. What it no longer implies
          is that the AI product is a way in, or that an AI subscription pays for this one.
        */}
        <p className="mt-1 mb-5" style={{ fontSize: "var(--text-dense)", color: "var(--muted)" }}>
          Sign in to PulseAssist Email. If you already have a PulseAssist account, it works here too.
        </p>

        {sso?.google.enabled && sso.google.clientId && (
          <div className="mb-4 flex flex-col gap-3">
            <GoogleButton
              clientId={sso.google.clientId}
              text="continue_with"
              disabled={busy}
              onCredential={(c) => void submitGoogle(c)}
            />
            {/* A divider, not a heading — the two paths are alternatives of equal standing. */}
            <div className="flex items-center gap-3" aria-hidden="true">
              <span className="h-px flex-1" style={{ background: "var(--border)" }} />
              <span className="micro" style={{ color: "var(--muted)" }}>or</span>
              <span className="h-px flex-1" style={{ background: "var(--border)" }} />
            </div>
          </div>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) void submit();
          }}
          className="flex flex-col gap-3"
        >
          <label className="flex flex-col gap-1.5">
            <span className="micro">Email</span>
            <input
              type="email"
              autoComplete="username"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-[34px] px-[10px]"
              style={{
                border: "1px solid var(--border)",
                borderRadius: "var(--radius)",
                background: "var(--surface)",
                fontSize: "var(--text-dense)",
                color: "var(--text)",
              }}
            />
          </label>

          <label className="flex flex-col gap-1.5">
            <span className="micro">Password</span>
            <span className="relative flex items-center">
              <input
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-[34px] w-full px-[10px] pr-9"
                style={{
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radius)",
                  background: "var(--surface)",
                  fontSize: "var(--text-dense)",
                  color: "var(--text)",
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                className="absolute right-2 flex items-center"
                style={{ color: "var(--muted)" }}
              >
                <Icon name={showPassword ? "eye-off" : "eye"} size={14} />
              </button>
            </span>
          </label>

          {/*
            Rendered only when the server said it wants one. `min-height` is reserved so the button does
            not jump down the page when the widget finishes loading.
          */}
          {captcha?.enabled && !captchaBroken && (
            <div ref={widgetHost} style={{ minHeight: 65 }} aria-label="Verification" />
          )}

          {/*
            The captcha provider is unreachable. Sign-in is still ATTEMPTABLE, because the server is the
            authority: if it insists, it refuses and says so. Blocking the form here would mean an
            outage at Cloudflare locks every customer out of their own console.
          */}
          {captchaBroken && (
            <p
              className="flex items-start gap-2 px-3 py-2"
              style={{
                background: "var(--warn-bg)",
                border: "1px solid var(--warn-border)",
                borderRadius: "var(--radius)",
                fontSize: "var(--text-meta)",
              }}
            >
              <Icon name="warning" size={13} style={{ marginTop: 1, color: "var(--st-deferred)" }} />
              <span>
                The verification widget could not load. You can still try to sign in — if verification is
                required, the attempt will say so.
              </span>
            </p>
          )}

          {error && (
            <p
              role="alert"
              className="flex items-start gap-2 px-3 py-2"
              style={{
                background: "var(--warn-bg)",
                border: "1px solid var(--st-bounced)",
                borderRadius: "var(--radius)",
                fontSize: "var(--text-meta)",
              }}
            >
              <Icon name="critical" size={13} style={{ marginTop: 1, color: "var(--st-bounced)" }} />
              <span>
                {error}
                {retryAfter !== null && (
                  <> Try again in {retryAfter < 60 ? `${retryAfter}s` : `${Math.ceil(retryAfter / 60)} min`}.</>
                )}
              </span>
            </p>
          )}

          <button
            type="submit"
            className="pa-btn pa-btn-primary mt-1"
            style={{ height: 34 }}
            disabled={!canSubmit}
          >
            {busy ? "Signing in…" : "Sign in"}
          </button>

          {captchaOutstanding && !captchaBroken && (
            <p style={{ fontSize: "var(--text-meta)", color: "var(--muted)" }}>
              Complete the verification above to continue.
            </p>
          )}
        </form>

        {/*
          Password reset lives on the main dashboard, and this links there rather than reimplementing
          it. One reset flow, one set of tokens, one place where the rate limiting and the email
          templates live — a second implementation would be a second thing to keep correct.
        */}
        {/*
          The way in for somebody who does not have an account yet.
          
          It has to be here, and prominently, because until now this screen was a dead end for a new
          Email customer: it asked for credentials, offered a password reset for an account that did
          not exist, and never once suggested creating one.
        */}
        <p className="mt-5" style={{ fontSize: "var(--text-dense)", color: "var(--muted)" }}>
          New to PulseAssist Email?{" "}
          <button
            type="button"
            onClick={onCreateAccount}
            style={{ color: "var(--accent)", fontWeight: 500 }}
          >
            Create an account
          </button>
        </p>

        <p className="mt-3" style={{ fontSize: "var(--text-meta)", color: "var(--muted)" }}>
          Forgotten your password? Reset it from the{" "}
          <a
            href="https://getpulseassist.com/forgot-password"
            style={{ color: "var(--accent)" }}
            rel="noreferrer"
          >
            PulseAssist dashboard
          </a>
          . If you signed up with Google, use the Google button above instead.
        </p>
      </main>
    </div>
  );
}
